import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import i18n from '../../../i18n';

const { t } = i18n;
interface ICancelSubState {
  success: boolean;
  showLoader: boolean;
  error: string;
  recaptchaPassed: boolean;
}
interface IPayload {
  email: string;
  orderName: string;
  extraordinaryReason?: string;
}

const initialState: ICancelSubState = {
  success: false,
  error: '',
  recaptchaPassed: false,
  showLoader: false,
};

export const sendCancelRequest = createAsyncThunk(
  'cancelSubscription/sendCancelRequest',
  async (body: IPayload) => {
    const url = `${process.env.REACT_APP_RENTAL_SERVICE_API}/self-service/subscriptions/request-cancellation`;
    const response = await fetch(url, {
      method: 'post',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email: body.email,
        orderName: body.orderName,
        extraordinaryReason: body.extraordinaryReason,
      }),
    });
    if (response.status >= 300) {
      switch (response.status) {
        case 400:
          throw new Error(
            t(
              '400_bad_request_error',
              "We're sorry, there was an error with your request. Please contact Customer Care at customercare@happiestbaby.com for futher assistance."
            )
          );
        case 404:
          throw new Error(
            t(
              'order_not_found',
              'We’re sorry, but we can’t find that order number. Please double check your order and email address and try again.'
            )
          );
        default:
          throw new Error(
            t(
              'generic_error',
              'Oops…something went wrong! Please check your email address and order number and try again. If you need help, please connect with us at customercare@happiestbaby.com.'
            )
          );
      }
    }
    if (response.status === 204) {
      return response;
    }
    return response.json();
  }
);
export const cancelSubscriptionSlice = createSlice({
  name: 'cancelSubscription',
  initialState,
  reducers: {
    recaptchaVerified: (
      state: ICancelSubState,
      action: PayloadAction<string | null>
    ) => {
      state.recaptchaPassed = !!action.payload;
    },
    gotError: (state: ICancelSubState, action: PayloadAction<string>) => {
      state.error = action.payload;
      state.success = false;
      state.showLoader = false;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(sendCancelRequest.pending, (state: ICancelSubState) => {
      state.showLoader = true;
    });
    builder.addCase(
      sendCancelRequest.rejected,
      (state: ICancelSubState, action) => {
        if (action.error.message) {
          state.error = action.error.message;
        } else {
          state.error = '';
        }
        state.success = false;
        state.showLoader = false;
      }
    );
    builder.addCase(sendCancelRequest.fulfilled, (state: ICancelSubState) => {
      state.error = '';
      state.success = true;
      state.showLoader = false;
    });
  },
});

export const { recaptchaVerified, gotError } = cancelSubscriptionSlice.actions;

export default cancelSubscriptionSlice.reducer;
